import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'auto'

const STORAGE_KEY = 'afd.theme'

type ResolvedTheme = 'light' | 'dark'

function readPersisted(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'auto') return raw
  } catch {
    /* ignore */
  }
  return 'auto'
}

function writePersisted(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    /* ignore */
  }
}

function detectSystem(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
  root.dataset.theme = resolved
  root.style.colorScheme = resolved
}

export const useThemeStore = defineStore('theme', () => {
  const mode = ref<ThemeMode>(readPersisted())
  const systemTheme = ref<ResolvedTheme>(detectSystem())

  const resolvedTheme = computed<ResolvedTheme>(() =>
    mode.value === 'auto' ? systemTheme.value : mode.value,
  )
  const isDark = computed(() => resolvedTheme.value === 'dark')

  function setMode(next: ThemeMode): void {
    mode.value = next
    writePersisted(next)
    applyTheme(resolvedTheme.value)
  }

  function cycleTheme(): void {
    const order: ThemeMode[] = ['light', 'dark', 'auto']
    const idx = order.indexOf(mode.value)
    setMode(order[(idx + 1) % order.length])
  }

  function applyResolved(): void {
    applyTheme(resolvedTheme.value)
  }

  function setSystemTheme(resolved: ResolvedTheme): void {
    systemTheme.value = resolved
    if (mode.value === 'auto') applyTheme(resolved)
  }

  return {
    mode,
    systemTheme,
    resolvedTheme,
    isDark,
    setMode,
    cycleTheme,
    applyResolved,
    setSystemTheme,
  }
})
